/**
 * The HTTP client, and the only place that knows where the API lives.
 *
 * This file is not in the repository this panel's UI came from -- it is
 * gitignored there -- so it is written for MySuhbat, against the Django admin
 * API. The contract the rest of the app expects is: a default export with
 * axios's verbs, and a named API_URL.
 *
 * The interesting part is refresh. An admin screen fires several requests at
 * once, so when an access token expires they all get a 401 at the same moment.
 * Refreshing per request would fire N parallel refreshes -- and because the
 * backend rotates refresh tokens and blacklists the old one, all but the first
 * would fail and sign the person out mid-session. So at most one refresh is
 * ever in flight and everybody else awaits the same promise.
 */
import axios from "axios";

// Same-origin by default: CRA proxies /api to Django in development and nginx
// does the same in production, so XHR is never cross-origin and CORS stays out
// of the way.
export const API_URL = process.env.REACT_APP_API_URL || "/api";

/**
 * The same API, addressed absolutely, for full-page navigations.
 *
 * CRA's dev server answers any request carrying `Accept: text/html` with
 * index.html before user middleware sees it -- so a *navigation* to /api/...
 * lands back in the app instead of reaching Django. Sending the browser to
 * Django's own origin sidesteps that, and a navigation is not subject to CORS.
 *
 * Empty in production, where nginx serves both from one origin.
 */
export const NAV_API_URL = `${process.env.REACT_APP_API_ORIGIN || ""}/api`;

const ACCESS = "mysuhbat.access";
const REFRESH = "mysuhbat.refresh";

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS);
  },
  get refresh() {
    return localStorage.getItem(REFRESH);
  },
  set(access, refresh) {
    if (access) localStorage.setItem(ACCESS, access);
    if (refresh) localStorage.setItem(REFRESH, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
  },
};

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const access = tokens.access;
  if (access) config.headers.Authorization = `Bearer ${access}`;
  return config;
});

let refreshing = null;

async function refreshOnce() {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const refresh = tokens.refresh;
    if (!refresh) return false;
    try {
      // A bare axios call, not `api`: going through the instance would hit the
      // interceptor below and recurse on its own 401.
      const res = await axios.post(`${API_URL}/auth/refresh/`, { refresh });
      tokens.set(res.data.access, res.data.refresh);
      return true;
    } catch {
      tokens.clear();
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    // `_retried` guards the one case a single retry cannot fix: a refresh that
    // succeeds but whose new access token is also refused. Without it that is
    // an infinite loop against the server.
    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      if (await refreshOnce()) return api(original);
    }
    return Promise.reject(error);
  },
);

export default api;
